import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
  STORAGE_KEYS,
} from "../../helpers";
import { usePostLoginMutation } from "../../service/auth";
import { showError, showToast } from "../../constants/toasts";
import { useAppDispatch } from "../../hooks/store";
import { setCredentials } from "../../reducers/authSlice";
import Loader from "../../constants/Loader";
import { getMessaging, getToken } from "firebase/messaging";
import { app } from "../../utils/firebaseKeys";

const Login = () => {
  const [loginMutation] = usePostLoginMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address"
        ),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      let fcm_token = "";
      try {
        setLoading(true);

        const messaging = getMessaging(app);

        // Register service worker
        const swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );

        // Try to get FCM token
        fcm_token = await getToken(messaging, {
          vapidKey:
            "BLtDoS2oJPW2cxTcxe_nPnO9vzmeFwK1g1KXhETAHCljX02TC8nMddp69iUg_sKjKpkPSAv0WptMe3055szLPhE",
          serviceWorkerRegistration: swRegistration,
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);

        console.warn(
          "Unable to get FCM token (permission denied or blocked)",
          err
        );
      }

      // Save token even if empty
      setToStorage(STORAGE_KEYS.fcmToken, fcm_token || "");

      const body = {
        email: values.email,
        password: values.password,
        deviceToken: fcm_token || "",
      };

      try {
        setLoading(true);
        // const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await loginMutation(body).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          showToast("Logged In Successfully.");
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          setToStorage(
            STORAGE_KEYS.userData,
            JSON.stringify(response?.data?.userData)
          );
          dispatch(
            setCredentials({
              user: response?.data?.userData || null,
              token: response?.data?.token || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          navigate("/dashboard");
        } else {
          setLoading(false);
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <Loader isLoad={loading} />
      <div className="authBox">
        <h2>Login</h2>
        <form onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label htmlFor="email">Email Address</label>
            <TextField
              placeholder="Enter Email Address"
              hiddenLabel
              name="email"
              id="email"
              className="text_field"
              fullWidth
              value={formik.values.email}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={
                formik.touched.email && typeof formik.errors.email === "string"
                  ? formik.errors.email
                  : ""
              }
            />
          </div>
          <div className="control_group">
            <label htmlFor="password">Password</label>
            <TextField
              placeholder="Enter Password"
              fullWidth
              hiddenLabel
              name="password"
              id="password"
              className="text_field"
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={
                formik.touched.password &&
                typeof formik.errors.password === "string"
                  ? formik.errors.password
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={<Checkbox />}
                checked={formik.values.remember}
                name="remember"
                onChange={formik.handleChange}
                label="Remember me"
              />
            </div>
            <a
              href="javacript:void(0)"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </a>
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary w_100"
              // onClick={() => navigate("/dashboard")}
              type="submit"
            >
              Login
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
